import { Helmet } from "react-helmet";
import styles from "./Messaging.module.scss";
import { useEffect, useRef, useState } from "react";
import { LiveChatMesagesTypes } from "../../interfaces/admin.interface";
import { LiveSupportService } from "../../services/live_support.service";
import { useParams } from "react-router-dom";
import Spinner from "../../components/shared/Spinner/Spinner";
import { FaPlus } from "react-icons/fa";
import Pusher from "pusher-js";
import { Image } from "antd";

const Messaging = () => {
  const [chats, setChats] = useState<LiveChatMesagesTypes[]>([]);
  const [loading, setLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [content, setContent] = useState("");
  const [userInfo, setUserInfo] = useState({
    user_name: "",
    user_email: "",
    user_phone: "",
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    setLoading(true);
    LiveSupportService.getChatById(Number(id))
      .then((res) => {
        const messages = res.data.messages;
        setChats(messages);
        setUserInfo(res.data.user_info);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    Pusher.logToConsole = true;
    const pusher = new Pusher("66fa74a20735682f38a2", {
      cluster: "ap2",
      forceTLS: true,
      authEndpoint: "/pusher/auth",
    });

    const channel = pusher.subscribe("chat-channel");

    pusher.connection.bind("state_change", (states: any) => {
      console.log("Pusher connection state:", states);
    });

    channel.bind("pusher:subscription_succeeded", () => {
      console.log("Successfully subscribed to channel!");
    });
    const eventName = `user-${id}`;
    console.log("Binding to event:", eventName);

    const handler = (data: any) => {
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;
      const newMessage = {
        content: parsedData.content,
        is_file: parsedData.is_file,
        sender_type: parsedData.sender_type,
        hour: parsedData.hour,
        date: parsedData.date,
      };
      addMessageInstantly(newMessage);
    };

    channel.bind(eventName, handler);

    return () => {
      channel.unbind(eventName, handler);
      channel.unsubscribe();
      pusher.disconnect();
    };
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chats]);

  const addMessageInstantly = (message: any) => {
    setChats((prev) => {
      if (prev.length === 0) {
        return [{ date: message.date || "", messages: [message] }];
      }
      const lastGroup = prev[prev.length - 1];
      if (!lastGroup.date || lastGroup.date !== message.date) {
        return [...prev, { date: message.date || "", messages: [message] }];
      }
      const updated = [...prev];
      updated[updated.length - 1] = {
        ...lastGroup,
        messages: [...lastGroup.messages, message],
      };
      return updated;
    });
  };

  const sendMessage = (isFile: number, messageContent: string | File) => {
    if (
      !messageContent ||
      (!isFile && (messageContent as string).trim() === "")
    )
      return;
    setSendLoading(true);
    setContent("");
    let payload: any;
    if (isFile) {
      payload = new FormData();
      payload.append("is_file", "1");
      payload.append("content", messageContent as File);
    } else {
      payload = { is_file: 0, content: messageContent };
    }

    LiveSupportService.sendMessage(Number(id), payload)
      .catch((err) => console.error(err))
      .finally(() => {
        setSendLoading(false);
        if (!isFile) setContent("");
      });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    sendMessage(1, file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div id="pages" className={styles.messaging_page}>
      <Helmet>
        <title>BSC | Mesajlaşma</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <>
          <div className="page_head">
            <h3 className="page_title">Mesajlaşma</h3>
          </div>

          <div className={styles.user_info}>
            <p className={styles.info}>{userInfo.user_name}</p>
            <p className={styles.info}>{userInfo.user_email}</p>
            <p className={styles.info}>{userInfo.user_phone}</p>
          </div>

          <div className={styles.pages_components}>
            {chats.length === 0 ? (
              <p className={styles.no_message}>Mesaj yoxdur.</p>
            ) : (
              chats.map((group, index) => (
                <div key={index} className={styles.chat_area}>
                  <div className={styles.date}>{group.date}</div>
                  {group?.messages?.map((item, idx) => {
                    return (
                      <div
                        key={idx}
                        className={`${styles.message} ${
                          item.sender_type === "user"
                            ? styles.user_msg
                            : styles.admin_msg
                        }`}
                      >
                        {item.is_file ? (
                          item.content.match(/\.(jpeg|jpg|gif|png|webp)$/) ? (
                            <Image
                              style={{ maxWidth: "100px" }}
                              src={item.content}
                              alt="Göndərilən şəkil"
                              className={styles.chat_image}
                            />
                          ) : (
                            <a
                              href={item.content}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {item.content.split("/").pop()}{" "}
                            </a>
                          )
                        ) : (
                          <p>{item.content}</p>
                        )}
                        <p
                          className={`${
                            item.sender_type === "user"
                              ? styles.user_time
                              : styles.admin_time
                          }`}
                        >
                          {item.hour}
                        </p>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              ))
            )}
            <form className={styles.input_area}>
              <div className={styles.input}>
                <input
                  type="text"
                  placeholder="Mesajınızı yazın..."
                  onChange={(e) => setContent(e.target.value)}
                  value={content}
                />
                <button
                  type="button"
                  className={styles.file_btn}
                  onClick={(e) => {
                    fileInputRef.current?.click();
                    e.preventDefault();
                  }}
                >
                  <FaPlus color="#0277bd" />
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                />
              </div>
              <button
                disabled={!content || sendLoading}
                type="submit"
                className={styles.send_btn}
                onClick={(e) => {
                  sendMessage(0, content);
                  e.preventDefault();
                }}
              >
                Göndər
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default Messaging;
